/**
 * Checks if a user has a specific module action or sidebar permission.
 * Evaluates direct permissions, custom roles, and system role legacy fallbacks.
 *
 * @param {Object} user - The authenticated user object from AuthContext
 * @param {string} permissionName - The permission string to check (e.g. 'lead:read_all')
 * @returns {boolean} - Whether the user has the permission
 */
export const hasPermission = (user, permissionName) => {
  if (!user) return false;
  if (user.role === 'admin') return true;

  // Handle array of alternate permissions (e.g. ['call_history:view_all', 'call_history:view_own'])
  if (Array.isArray(permissionName)) {
    return permissionName.some(p => hasPermission(user, p));
  }

  // 1. Direct permissions array if present
  if (Array.isArray(user.permissions) && user.permissions.length > 0) {
    if (user.permissions.includes(permissionName) || user.permissions.includes('*')) return true;
  }

  // 2. Custom Role Check (if roleDetails is populated object)
  if (user.roleDetails && typeof user.roleDetails === 'object') {
    const customPerms = user.roleDetails?.permissions;
    if (Array.isArray(customPerms) && customPerms.length > 0) {
      return customPerms.some(p => (typeof p === 'object' ? p.name : p) === permissionName);
    }
    if (Array.isArray(customPerms) && customPerms.length === 0) {
      return false;
    }
  }

  // 3. System Role Legacy Fallbacks
  const systemPermissionMap = {
    customer_care: ['lead:create', 'lead:read_all', 'lead:read_assigned', 'lead:update', 'lead:assign', 'appointment:create', 'appointment:read', 'appointment:update', 'surgery:view', 'call:initiate', 'call:receive', 'call_history:view_own', 'ophe:view', 'ophe:create', 'ophe:update', 'sidebar:ophe_services'],
    cc_head: ['lead:create', 'lead:read_all', 'lead:read_assigned', 'lead:update', 'lead:assign', 'lead:delete', 'appointment:create', 'appointment:read', 'appointment:update', 'appointment:delete', 'surgery:view', 'surgery:schedule', 'surgery:update_status', 'report:view', 'call:initiate', 'call:receive', 'call_history:view_all', 'ophe:view', 'ophe:create', 'ophe:update', 'sidebar:ophe_services'],
    doctor: ['lead:read_assigned', 'surgery:view', 'surgery:verify_otp', 'surgery:update_status', 'payout:view_own'],
    hospital: ['lead:read_assigned', 'surgery:view', 'surgery:update_status', 'payout:view_own'],
    marketing_agent: ['lead:create', 'lead:read_assigned', 'payout:view_own', 'call:initiate', 'call_history:view_own'],
    accountant: ['lead:read_all', 'lead:read_assigned', 'appointment:read', 'appointment:create', 'appointment:update', 'surgery:view', 'payout:view_all', 'payout:approve', 'payout:process', 'report:view'],
    receptionist: ['lead:create', 'lead:read_all', 'lead:read_assigned', 'lead:update', 'appointment:create', 'appointment:read', 'appointment:update', 'call:initiate', 'call:receive', 'call_history:view_own'],
    staff: ['lead:read_assigned', 'appointment:read', 'surgery:view']
  };

  let roleKey = user.role;
  if (!systemPermissionMap[roleKey]) {
    const lowerRole = (user.role || '').toLowerCase();
    if (lowerRole.includes('accountant')) roleKey = 'accountant';
    else if (lowerRole.includes('receptionist')) roleKey = 'receptionist';
    else if (lowerRole.includes('staff')) roleKey = 'staff';
    else if (lowerRole.includes('care') || lowerRole.includes('cc')) roleKey = 'customer_care';
    else if (lowerRole.includes('doctor')) roleKey = 'doctor';
    else if (lowerRole.includes('hospital')) roleKey = 'hospital';
    else if (lowerRole.includes('agent') || lowerRole.includes('partner')) roleKey = 'marketing_agent';
  }

  return systemPermissionMap[roleKey]?.includes(permissionName) || false;
};
